import { useState } from 'react';

import Misc from '@components/Misc';
import { VectorInput, WrapperEntitySelector } from '@components/Inputs';
import { Grid, TextInput, Modal, Typography, Checkbox, IconButton, Icon } from '@ts/storybook';
import EntityTableSelector from '@pages/DataBase/Views/ViewLayerSelector';
import { useAppSelector } from '@redux/store';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import WithAllPropsApp from '@utils/wrappers/WithAllPropsApp';
import { CView, IForm } from '@ts/ui-models';
import { useTranslation } from 'react-i18next';
import { CLayer } from '@models/maps/Layer';

const validateSize = (sizeX, sizeY) => {
  let error = '';
  if (sizeX === 0 || sizeY === 0) {
    error = 'Invalid vector, no component can be 0 ';
  }

  return error;
};

interface IViewFormProps extends IForm {
  values: CView;
}

const ViewForm = (props: IViewFormProps) => {
  const { mode, values, onCallbackChange } = props;
  const { t } = useTranslation();
  const isViewMode = mode === 'view';

  let _units = ['', ''];
  const [state, setState] = useState<any>({
    showModal: false,
    now: !props.values.properties.dateRange?.to,
    isMapOutdoor: props.values.viewTypeId === 'map2',
  });
  const settings = useAppSelector(state => state.global.settings);

  //#region Handlers
  const _handleChangeEntitySelector = (entity, entityName, fkField) => {
    const value = entity ? entity.getID() : null;
    onCallbackChange(entityName)(entity);
    return onCallbackChange(fkField)(value);
  };

  const onShowXViewLayerPropsModal = entity => {
    const { xViewLayers } = props.values;
    const xViewLayer = xViewLayers?.find(xView => xView.layer.logicalId === entity.logicalId);
    setState({ ...state, showModal: true, xViewLayer });
  };

  const onCloseViewLayerModal = () => {
    const { xViewLayer, xViewLayerChanged } = state;
    if (!xViewLayerChanged) return setState({ ...state, xViewLayer: null, showModal: false });
    const { xViewLayers: xViewLayers0 } = props.values;
    const xViewLayers = [...(xViewLayers0 || [])];
    const xViewLayerToModifyIdx = xViewLayers.findIndex(xViewToModify => xViewToModify.logicalId === xViewLayer.logicalId);
    xViewLayers[xViewLayerToModifyIdx] = { ...xViewLayer };
    onCallbackChange('xViewLayers')(xViewLayers);
    setState({ ...state, showModal: false, xViewLayer: null });
  };

  const onChangeXViewLayerProps = field => value => {
    const { xViewLayer: xViewLayer0 } = state;
    const xViewLayer = { ...xViewLayer0 };
    xViewLayer.properties = { ...xViewLayer.properties, [field]: value };
    setState({ ...state, xViewLayer, xViewLayerChanged: true });
  };

  const onAddViewLayer = newLayer => {
    if (!newLayer) return;
    const { logicalId, xViewLayers } = props.values;
    onCallbackChange('xViewLayers')(
      xViewLayers?.concat({
        logicalId: newLayer.logicalId,
        background: newLayer.backgroundId,
        viewId: logicalId,
        properties: {
          layerCenterCoord: [0, 0],
          layerSize: [null, null],
        },
        layer: newLayer,
      })
    );
  };

  const onRemoveViewLayer = layerToRemove => {
    const { xViewLayers } = props.values;
    onCallbackChange('xViewLayers')(xViewLayers?.filter(xViewLayer => xViewLayer.layer.id !== layerToRemove.id));
  };

  //#endregion

  //#region Render
  const renderModalXViewLayerProperties = () => {
    const { values } = props;
    const { showModal, xViewLayer } = state;
    if (!showModal || !xViewLayer) return;
    if (state.isMapOutdoor) {
      return (
        <Modal
          withHeader
          open={showModal}
          title="View layer"
          disabled={isViewMode}
          onClose={() => setState({ ...state, showModal: false, xViewLayer: null })}
          onSubmit={onCloseViewLayerModal}
        >
          <Grid container spacing={2} p={2}>
            <Grid size={{ xs: 12 }}>
              <VectorInput
                labelX={`${t('Vertex bottom left')} ${_units[0]}`}
                labelY={`${t('Vertex bottom left')} ${_units[1]}`}
                value={xViewLayer.properties.vertexBottomLeft}
                onChange={onChangeXViewLayerProps('vertexBottomLeft')}
                required
                fDisabled={isViewMode}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <VectorInput
                labelX={`${t('Vertex top left')} ${_units[0]}`}
                labelY={`${t('Vertex top left')} ${_units[1]}`}
                value={xViewLayer.properties.vertexTopLeft}
                onChange={onChangeXViewLayerProps('vertexTopLeft')}
                required
                fDisabled={isViewMode}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <VectorInput
                labelX={`${t('Vertex top right')} ${_units[0]}`}
                labelY={`${t('Vertex top right')} ${_units[1]}`}
                value={xViewLayer.properties.vertexTopRight}
                onChange={onChangeXViewLayerProps('vertexTopRight')}
                required
                fDisabled={isViewMode}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <VectorInput
                labelX={`${t('Vertex bottom right')} ${_units[0]}`}
                labelY={`${t('Vertex bottom right')} ${_units[1]}`}
                value={xViewLayer.properties.vertexBottomRight}
                onChange={onChangeXViewLayerProps('vertexBottomRight')}
                required
                fDisabled={isViewMode}
              />
            </Grid>
          </Grid>
        </Modal>
      );
    }
    return (
      <Modal
        withHeader
        open={showModal}
        title="View layer"
        disabled={isViewMode}
        onSubmit={onCloseViewLayerModal}
        onClose={() => setState({ ...state, showModal: false, xViewLayer: null })}
      >
        <Grid container spacing={2} p={2}>
          <Grid size={{ xs: 12 }}>
            <VectorInput
              labelX={`${t('Center of coordinate system')} ${_units[0]}`}
              labelY={`${t('Center of coordinate system')} ${_units[1]}`}
              value={xViewLayer.properties.layerCenterCoord}
              onChange={onChangeXViewLayerProps('layerCenterCoord')}
              required
              fDisabled={isViewMode}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <VectorInput
              labelX={t('Layer width in meters')}
              labelY={t('Layer height in meters')}
              value={xViewLayer.properties.layerSize}
              onChange={onChangeXViewLayerProps('layerSize')}
              required={values.viewTypeId === '2d'}
              helper={
                xViewLayer.properties.layerSize &&
                validateSize(xViewLayer.properties.layerSize[0], xViewLayer.properties.layerSize[1])
              }
              fDisabled={isViewMode}
            />
          </Grid>
        </Grid>
      </Modal>
    );
  };
  //#endregion

  const viewType = values.viewType;

  const viewTypeCoordinates = viewType?.coordinates;

  if (viewTypeCoordinates === 'GPS') {
    _units = [t('Longitude in decimal degrees'), t('Latitude in decimal degrees')];
  } else if (viewTypeCoordinates === 'XY') {
    _units = [t('X in meters'), t('Y in meters')];
  }
  const defaultView = values.defaultView ?? settings?.defaultView?.defaultViewId === values.logicalId;

  return (
    <Grid container justifyContent="center">
      <Grid container size={{ xs: 12, md: 8 }} spacing={2} justifyContent="flex-start">
        <Grid size={{ xs: 12 }} mt={2}>
          <Misc.NewPaperWithPadding>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ mb: 2 }} text="Main information" />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextInput
                  isReadOnly={isViewMode}
                  label="Name"
                  value={values.name}
                  onChangeValue={onCallbackChange('name')}
                  required={props.requiredFields?.includes('viewType')}
                  infoText="Blank spaces at the beggining and the end will be trimmed on save"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <WrapperEntitySelector
                  label={'View Type'}
                  value={values.viewType ?? values.viewTypeId}
                  onChangeEntity={entity => _handleChangeEntitySelector(entity, 'viewType', 'viewTypeId')}
                  searchComponent={generateSearchEntitySelector('viewType')}
                  isReadOnly={isViewMode}
                  required={props.requiredFields?.includes('viewType')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Checkbox
                  disabled={
                    isViewMode ||
                    ((settings?.defaultView?.level === 'organization' || settings?.defaultView?.level === 'user') &&
                      settings?.defaultView?.defaultViewId === values.logicalId)
                  }
                  label="Default view map"
                  checked={defaultView}
                  onChangeValue={onCallbackChange('defaultView')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <WrapperEntitySelector
                  label={'Default location'}
                  value={values.location ?? values.locationId}
                  onChangeEntity={entity => _handleChangeEntitySelector(entity, 'location', 'locationId')}
                  searchComponent={generateSearchEntitySelector('location')}
                  isReadOnly={isViewMode}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextInput
                  isReadOnly={isViewMode}
                  disabled={isViewMode}
                  label="Notes"
                  value={values.notes}
                  onChangeValue={onCallbackChange('notes')}
                  multiline
                />
              </Grid>
            </Grid>
          </Misc.NewPaperWithPadding>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Misc.NewPaperWithPadding>
            <Typography variant="h6" sx={{ mb: 2 }} text="Layers" />
            <Grid>
              <EntityTableSelector
                {...props}
                entitiesSelected={values.xViewLayers?.filter(v => !!v)?.map(xViewLayer => new CLayer(xViewLayer.layer))}
                onEntitySelected={onAddViewLayer}
                onEntityRemoved={onRemoveViewLayer}
                renderEntityActions={(entity, idx) => (
                  <div>
                    {entity.background || entity.backgroundId ? (
                      <IconButton
                        onClick={() => {
                          onShowXViewLayerPropsModal(entity);
                        }}
                      >
                        <Icon icon="ZoomOutMap" />
                      </IconButton>
                    ) : null}
                    <IconButton
                      disabled={isViewMode}
                      onClick={() => {
                        onRemoveViewLayer(entity);
                      }}
                    >
                      <Icon icon="action-delete" />
                    </IconButton>
                  </div>
                )}
              />
            </Grid>
          </Misc.NewPaperWithPadding>
        </Grid>
      </Grid>

      {renderModalXViewLayerProperties()}
    </Grid>
  );
};

export default WithAllPropsApp(ViewForm);
